import { Icon, Menu, MenuItem } from "@material-ui/core";
import { AddCircleOutlineOutlined as AddIcon } from "@material-ui/icons";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import AppController from "js/core/AppController";
import { openTeamUpgrade } from "js/core/utilities/externalLinks";
import { app } from "js/namespaces";
import Avatar from "js/react/components/Avatar";
import GuestBadge from "js/react/components/GuestBadge";
import LeaveWorkspace from "js/react/components/LeaveWorkspace";
import SearchInput from "js/react/components/SearchInput";
import { FlexSpacer, Gap15 } from "js/react/components/Gap";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";
import useWorkspaceMenuFocusNavigation from "./utils/useWorkspaceMenuFocusNavigation";

const WorkspaceSwitcherTrigger = styled.div`
    border: 1px solid #666;
    border-radius: 3px;
    color: #FFF;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;

    cursor: pointer;
`;

const WorkspaceTriggerName = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #FFF;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    min-width: 0;
`;

const TriggerContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;
    min-width: 0;
`;

const WorkspaceNameText = styled.div<{ $isSelected?: boolean }>`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ $isSelected }) => ($isSelected ? themeColors.ui_blue : "inherit")};
`;

const SelectedWorkspaceIcon = styled(Icon)`
    &&& {
        color: ${themeColors.ui_blue};
        margin-right: 0;
    }
`;

const WorkspaceSwitcherMenuItem = styled(MenuItem)`
    &&& {
        display: flex;
        align-items: center;
        gap: 10px;
        justify-content: space-between;

        :hover {
            color: #fff;

            ${SelectedWorkspaceIcon} {
                color: #fff;
            }

            ${WorkspaceNameText} {
                color: #fff;
            }
        }
    }
`;

const StyledMenu = styled(Menu)`
    &&& {
        width: auto;
        .MuiPaper-root {
            max-height: 80vh;
            display: flex;
            flex-direction: column;
        }
        .MuiList-root {
            display: flex;
            flex-direction: column;
            max-height: inherit;
            overflow: hidden;
            width: 100%;
        }
        .MuiMenuItem-root {
            width: 100%;
            box-sizing: border-box;
        }
    }
`;

const WorkspaceItemLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
`;

const SearchContainer = styled.div`
    padding: 5px 15px;
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const WorkspaceTitle = styled.div`
    color: #999;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const MenuSearchInput = styled(SearchInput)`
    position: relative;
    display: flex;

    &.searchbar {
        align-items: center;
        padding: 8px 10px;
        border: 1px solid #EEE;
    }

    & > .micon:first-child {
        font-size: 16px;
        margin-right: 10px;
        margin-left: 5px;
        color: #11a9e2;
    }

    input[type="text"] {
        color: #333;
        width: 100%;
        font-size: 14px;
        font-weight: normal;
        border: none;
        outline: none;
        background: none;
        padding-right: 32px;

        &::placeholder {
            font-weight: 500;
        }
    }

    .clear_search {
        position: absolute;
        right: 8px;
        color: #999;
        font-size: 16px;
        cursor: pointer;
    }
`;

const NoResultsMessage = styled.div`
    padding: 15px 20px;
    text-align: center;
    color: #666;
    font-size: 12px;
    font-style: italic;
`;

const ScrollableMenuContent = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`;

const StickyMenuFooter = styled.div`
    flex-shrink: 0;
    border-top: 1px solid #e0e0e0;
    background: white;
`;

const CreateWorkspaceMenuItem = styled(MenuItem)`
    &&& {
        display: flex;
        align-items: center;
    }
`;

interface Props {
    workspaceId: string;
}

export default function PresentationLibraryWorkspaceDropdown({ workspaceId }: Props) {
    const { setFocusableItemRef, handleSearchKeyDown, handleWorkspaceListKeyDownCapture } = useWorkspaceMenuFocusNavigation();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState("");
    const allWorkspacesMetadata: any[] = AppController.user?.workspacesMetadata ?? [];
    const currentWorkspace = allWorkspacesMetadata.find(workspace => workspace.id === workspaceId);
    const canSwitchWorkspace = allWorkspacesMetadata.length > 1;

    const { filteredNonGuestWorkspaces, filteredGuestWorkspaces, hasNoSearchResults } = useMemo(() => {
        const nonGuestWorkspaces = allWorkspacesMetadata
            .filter(m => m.userRole !== WorkspaceUserRole.GUEST)
            .sort((a, b) => a.plan.tier.localeCompare(b.plan.tier));

        const workspacesByTier = Object.values(WorkspacePlanTier).reduce<Record<string, any[]>>((acc, planTier) => {
            const workspacesForTier = nonGuestWorkspaces.filter(workspace => workspace.plan.tier === planTier);
            if (workspacesForTier.length > 0) {
                acc[planTier] = workspacesForTier;
            }
            return acc;
        }, {});

        Object.values(workspacesByTier).forEach((workspaces: any[]) => {
            workspaces.sort((a, b) => a.createdAt - b.createdAt);
        });

        const orderedNonGuestWorkspaces: any[] = Object.values(workspacesByTier).flat() as any[];
        const guestWorkspaces = allWorkspacesMetadata
            .filter(m => m.userRole === WorkspaceUserRole.GUEST)
            .sort((a, b) => a.createdAt - b.createdAt);

        const searchQuery = workspaceSearchQuery.trim().toLowerCase();
        const filteredNonGuest = orderedNonGuestWorkspaces.filter(ws => !searchQuery || ws.name.toLowerCase().includes(searchQuery));
        const filteredGuest = guestWorkspaces.filter(ws => !searchQuery || ws.name.toLowerCase().includes(searchQuery));

        return {
            filteredNonGuestWorkspaces: filteredNonGuest,
            filteredGuestWorkspaces: filteredGuest,
            hasNoSearchResults: Boolean(searchQuery) && filteredNonGuest.length === 0 && filteredGuest.length === 0
        };
    }, [allWorkspacesMetadata, workspaceSearchQuery]);

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setWorkspaceSearchQuery("");
    };

    const handleSwitchWorkspace = (nextWorkspaceId: string) => {
        handleCloseMenu();
        if (window.roomID && window.roomWorkspaceSwitched) {
            window.roomWorkspaceSwitched(nextWorkspaceId);
            return;
        }
        AppController.switchWorkspace(nextWorkspaceId);
    };

    const handleCreateWorkspace = () => {
        handleCloseMenu();
        openTeamUpgrade();
    };

    if (!currentWorkspace) {
        return null;
    }

    return (
        <>
            <WorkspaceSwitcherTrigger data-test-id="workspace-switcher-trigger" onClick={event => setAnchorEl(event.currentTarget)}>
                <TriggerContent>
                    <WorkspaceTriggerName>{currentWorkspace.name}</WorkspaceTriggerName>
                    <FlexSpacer />
                    {currentWorkspace.userRole === WorkspaceUserRole.GUEST && <GuestBadge />}
                </TriggerContent>
                <Icon>{anchorEl ? "arrow_drop_up" : "arrow_drop_down"}</Icon>
            </WorkspaceSwitcherTrigger>
            <StyledMenu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                autoFocus={false}
                disableAutoFocusItem
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
                onClose={handleCloseMenu}
                PaperProps={{ style: { minWidth: 320, maxWidth: 380 } }}
            >
                {canSwitchWorkspace && (
                    <SearchContainer onKeyDown={handleSearchKeyDown}>
                        <WorkspaceTitle>{t("Workspaces")}</WorkspaceTitle>
                        <MenuSearchInput
                            showIcon={false}
                            focus
                            placeholder={t("Search workspaces...")}
                            onChange={query => setWorkspaceSearchQuery(query || "")}
                            handleClearSearch={() => setWorkspaceSearchQuery("")}
                        />
                    </SearchContainer>
                )}
                <ScrollableMenuContent id="presentation-library-workspace-list" onKeyDownCapture={handleWorkspaceListKeyDownCapture}>
                    {filteredNonGuestWorkspaces.map((ws, index) => (
                        <WorkspaceSwitcherMenuItem
                            className="workspace-menu-item"
                            key={ws.id}
                            ref={setFocusableItemRef(index)}
                            disabled={!canSwitchWorkspace && ws.id !== workspaceId}
                            onClick={() => handleSwitchWorkspace(ws.id)}
                        >
                            <WorkspaceItemLeft>
                                {ws.id === workspaceId
                                    ? <SelectedWorkspaceIcon>check</SelectedWorkspaceIcon>
                                    : <Avatar url={AppController.userAvatarUrl} style={{ width: "24px", height: "24px" }} />
                                }
                                <WorkspaceNameText $isSelected={ws.id === workspaceId}>{ws.name}</WorkspaceNameText>
                            </WorkspaceItemLeft>
                            <LeaveWorkspace color="#000000" workspaceId={ws.id} onWorkspaceLeft={handleCloseMenu} />
                        </WorkspaceSwitcherMenuItem>
                    ))}
                    {filteredGuestWorkspaces.map((ws, index) => (
                        <WorkspaceSwitcherMenuItem
                            className="workspace-menu-item"
                            key={ws.id}
                            ref={setFocusableItemRef(filteredNonGuestWorkspaces.length + index)}
                            disabled={!canSwitchWorkspace && ws.id !== workspaceId}
                            onClick={() => handleSwitchWorkspace(ws.id)}
                        >
                            <WorkspaceItemLeft>
                                {ws.id === workspaceId
                                    ? <SelectedWorkspaceIcon>check</SelectedWorkspaceIcon>
                                    : <Avatar url={ws.workspacePhotoURL || AppController.userAvatarUrl} style={{ width: "24px", height: "24px" }} />
                                }
                                <WorkspaceNameText $isSelected={ws.id === workspaceId}>{ws.name}</WorkspaceNameText>
                            </WorkspaceItemLeft>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <GuestBadge />
                                <LeaveWorkspace color="#000000" workspaceId={ws.id} onWorkspaceLeft={handleCloseMenu} />
                            </div>
                        </WorkspaceSwitcherMenuItem>
                    ))}
                    {hasNoSearchResults && (
                        <NoResultsMessage>{t("No workspaces found")}</NoResultsMessage>
                    )}
                </ScrollableMenuContent>
                {!app.integrator && !window.isZoomApp && (
                    <StickyMenuFooter>
                        <CreateWorkspaceMenuItem onClick={handleCreateWorkspace}>
                            <AddIcon style={{ marginRight: 0 }} />
                            <Gap15 />
                            <span>{t("New Workspace...")}</span>
                        </CreateWorkspaceMenuItem>
                    </StickyMenuFooter>
                )}
            </StyledMenu>
        </>
    );
}
